/**
 * Boss-Specific Visual Effects Filter
 * Each boss gets a unique WebGL-style effect that matches their vibe
 * - Colossus: Heavy impact with red tint and screen shake
 * - Swarm Queen: Toxic glitch with purple pixelation
 * - Void Construct: Dark vortex with cyan distortion
 * - Omega Core: Energy overload with chromatic aberration
 */

export type BossVariant = 0 | 1 | 2 | 3 | 4;

export class BossEffectFilter {
  private overlay: HTMLDivElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D | null;
  private enabled = false;
  private currentVariant: BossVariant = 0;
  private animationFrameId: number | null = null;
  private cleanupTimeoutId: number | null = null;
  private time = 0;
  private intensity = 0; // 0 to 1 for fade in/out
  private targetIntensity = 0;
  private fadeSpeed = 2.0; // Units per second

  constructor() {
    this.createSVGFilters();

    // Create overlay container
    this.overlay = document.createElement('div');
    this.overlay.id = 'boss-effect-overlay';
    this.overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      pointer-events: none;
      z-index: 999998;
      display: none;
      overflow: hidden;
    `;

    // Create animated canvas layer
    this.canvas = document.createElement('canvas');
    this.canvas.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
    `;
    this.ctx = this.canvas.getContext('2d', { alpha: true });

    this.overlay.appendChild(this.canvas);
    document.body.appendChild(this.overlay);

    this.resizeCanvas();
    window.addEventListener('resize', () => this.resizeCanvas());

    this.addStyles();
  }

  private createSVGFilters(): void {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.style.cssText = 'position: absolute; width: 0; height: 0;';
    svg.innerHTML = `
      <defs>
        <!-- Tiny Tyrant: Magical sparkles with soft rainbow -->
        <filter id="boss-tyrant-filter">
          <feColorMatrix type="matrix" values="
            1.0 0 0 0 0
            0 1.05 0 0 0
            0 0 1.0 0 0
            0 0 0 1 0
          "/>
          <feGaussianBlur stdDeviation="0.1"/>
        </filter>

        <!-- Colossus: Barrel distortion with red tint -->
        <filter id="boss-colossus-filter">
          <feColorMatrix type="matrix" values="
            1.3 0 0 0 0
            0 0.7 0 0 0
            0 0 0.7 0 0
            0 0 0 1 0
          "/>
          <feGaussianBlur stdDeviation="0.5"/>
          <feTurbulence type="turbulence" baseFrequency="0.01" numOctaves="2" result="turbulence"/>
          <feDisplacementMap in2="turbulence" in="SourceGraphic" scale="3" xChannelSelector="R" yChannelSelector="G"/>
        </filter>

        <!-- Swarm Queen: Pixelation with purple hue -->
        <filter id="boss-swarm-filter">
          <feColorMatrix type="matrix" values="
            0.8 0 0 0 0
            0 0.6 0 0 0
            0 0 1.2 0 0
            0 0 0 1 0
          "/>
          <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="3" result="noise"/>
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="2" xChannelSelector="R" yChannelSelector="G"/>
        </filter>

        <!-- Void Construct: Vortex distortion with cyan -->
        <filter id="boss-void-filter">
          <feColorMatrix type="matrix" values="
            0.6 0 0 0 0
            0 0.85 0 0 0
            0 0 1.2 0 0
            0 0 0 1 0
          "/>
          <feGaussianBlur stdDeviation="0.3"/>
          <feTurbulence type="turbulence" baseFrequency="0.02" numOctaves="2" result="turbulence"/>
          <feDisplacementMap in2="turbulence" in="SourceGraphic" scale="3" xChannelSelector="R" yChannelSelector="G"/>
        </filter>

        <!-- Omega Core: Chromatic aberration with energy - MINIMAL SATURATION -->
        <filter id="boss-omega-filter">
          <feColorMatrix type="matrix" values="
            1.05 0 0 0 0
            0 0.97 0 0 0
            0 0 1.0 0 0
            0 0 0 1 0
          "/>
          <feGaussianBlur stdDeviation="0.2"/>
        </filter>
      </defs>
    `;
    document.body.appendChild(svg);
  }

  private addStyles(): void {
    if (document.getElementById('boss-effect-styles')) return;

    const style = document.createElement('style');
    style.id = 'boss-effect-styles';
    style.textContent = `
      /* Colossus Effect - Heavy Impact */
      body.boss-effect-colossus {
        filter: url(#boss-colossus-filter) contrast(1.2) brightness(0.9);
        animation: boss-shake 0.1s infinite;
        transition: filter 0.3s ease-out;
      }

      body.boss-effect-colossus::before {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: radial-gradient(
          ellipse at center,
          rgba(139, 0, 0, 0) 0%,
          rgba(139, 0, 0, 0.1) 70%,
          rgba(139, 0, 0, 0.2) 100%
        );
        pointer-events: none;
        z-index: 999997;
        animation: boss-pulse 2s ease-in-out infinite;
      }

      /* Swarm Queen Effect - Toxic Glitch */
      body.boss-effect-swarm {
        filter: url(#boss-swarm-filter) contrast(1.1);
        animation: boss-glitch 0.3s infinite;
        transition: filter 0.3s ease-out;
      }

      body.boss-effect-swarm::before {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: 
          repeating-linear-gradient(
            0deg,
            rgba(75, 0, 130, 0) 0px,
            rgba(75, 0, 130, 0.05) 2px,
            rgba(75, 0, 130, 0) 4px
          ),
          radial-gradient(
            ellipse at center,
            rgba(147, 112, 219, 0) 30%,
            rgba(147, 112, 219, 0.1) 100%
          );
        pointer-events: none;
        z-index: 999997;
        animation: boss-scan 3s linear infinite;
      }

      /* Void Construct Effect - Dark Distortion */
      body.boss-effect-void {
        filter: url(#boss-void-filter) brightness(0.85) contrast(1.3);
        animation: boss-void-distort 3s ease-in-out infinite;
        transition: filter 0.3s ease-out;
      }

      body.boss-effect-void::before {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: 
          radial-gradient(
            ellipse at 30% 40%,
            rgba(0, 0, 0, 0) 0%,
            rgba(0, 0, 0, 0.05) 100%
          ),
          radial-gradient(
            ellipse at 70% 60%,
            rgba(0, 255, 255, 0.03) 0%,
            rgba(0, 0, 0, 0) 50%
          ),
          linear-gradient(
            180deg,
            rgba(0, 0, 0, 0.05) 0%,
            rgba(25, 25, 112, 0.08) 50%,
            rgba(0, 0, 0, 0.1) 100%
          );
        pointer-events: none;
        z-index: 999997;
        animation: boss-void-darkness 5s ease-in-out infinite;
      }

      body.boss-effect-void::after {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: 
          repeating-linear-gradient(
            45deg,
            transparent 0px,
            rgba(0, 255, 255, 0.01) 2px,
            transparent 4px
          );
        pointer-events: none;
        z-index: 999996;
        animation: boss-void-shimmer 4s linear infinite;
      }

      /* Omega Core Effect - Energy Overload (Minimal) */
      body.boss-effect-omega {
        filter: url(#boss-omega-filter) contrast(1.0) brightness(1.0);
        animation: boss-energy 0.15s infinite;
        transition: filter 0.3s ease-out;
      }

      body.boss-effect-omega::before {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: radial-gradient(
          ellipse at center,
          rgba(255, 0, 0, 0.03) 0%,
          rgba(255, 255, 0, 0.02) 50%,
          rgba(255, 165, 0, 0.04) 100%
        );
        pointer-events: none;
        z-index: 999997;
        animation: boss-energy-pulse 1s ease-in-out infinite;
      }

      /* Chromatic aberration effect for Omega - Heavily Reduced */
      body.boss-effect-omega * {
        text-shadow: 
          -1px 0 0 rgba(255, 0, 0, 0.1),
          1px 0 0 rgba(0, 255, 255, 0.1),
          0 0 5px rgba(255, 255, 0, 0.05);
      }

      /* Animations */
      @keyframes boss-shake {
        0%, 100% { transform: translate(0, 0) rotate(0deg); }
        25% { transform: translate(-1px, 1px) rotate(0.2deg); }
        50% { transform: translate(1px, -1px) rotate(-0.2deg); }
        75% { transform: translate(-1px, -1px) rotate(0.1deg); }
      }

      @keyframes boss-pulse {
        0%, 100% { opacity: 0.6; }
        50% { opacity: 1; }
      }

      @keyframes boss-glitch {
        0% { transform: translate(0, 0); }
        20% { transform: translate(-1px, 1px); }
        40% { transform: translate(1px, -1px); }
        60% { transform: translate(-1px, -1px); }
        80% { transform: translate(1px, 1px); }
        100% { transform: translate(0, 0); }
      }

      @keyframes boss-scan {
        0% { transform: translateY(-100%); }
        100% { transform: translateY(100%); }
      }

      @keyframes boss-void-distort {
        0%, 100% { 
          filter: url(#boss-void-filter) brightness(0.85) contrast(1.3);
        }
        50% { 
          filter: url(#boss-void-filter) brightness(0.8) contrast(1.35);
        }
      }

      @keyframes boss-void-darkness {
        0%, 100% { opacity: 0.7; }
        50% { opacity: 0.9; }
      }

      @keyframes boss-void-shimmer {
        0% { transform: translateX(0); }
        100% { transform: translateX(20px); }
      }

      @keyframes boss-energy {
        0%, 100% { transform: translate(0, 0); }
        25% { transform: translate(-1px, 1px); }
        75% { transform: translate(1px, -1px); }
      }

      @keyframes boss-energy-pulse {
        0%, 100% { opacity: 0.8; transform: scale(1); }
        50% { opacity: 1; transform: scale(1.05); }
      }

      /* Fade transitions */
      .boss-effect-fade-in {
        animation: boss-fade-in 1s ease-out forwards;
      }

      .boss-effect-fade-out {
        animation: boss-fade-out 1s ease-out forwards;
      }

      @keyframes boss-fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes boss-fade-out {
        from { opacity: 1; }
        to { opacity: 0; }
      }

      /* Disable screen shake animations when setting is off */
      body.no-screen-shake.boss-effect-colossus {
        animation: none !important;
      }

      body.no-screen-shake.boss-effect-swarm {
        animation: none !important;
      }

      body.no-screen-shake.boss-effect-void {
        animation: none !important;
      }

      body.no-screen-shake.boss-effect-omega {
        animation: none !important;
      }

      /* Tiny Tyrant Effect - Magical Sparkles (NO SHAKE EVER) */
      body.boss-effect-tyrant {
        filter: url(#boss-tyrant-filter) brightness(1.05) contrast(0.95);
        animation: none !important; /* Never shake for tutorial boss */
        transition: filter 0.5s ease-out;
      }

      body.boss-effect-tyrant::before {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: 
          radial-gradient(
            ellipse at center,
            rgba(168, 230, 207, 0.08) 0%,
            rgba(255, 211, 182, 0.06) 50%,
            rgba(255, 170, 165, 0.05) 100%
          );
        pointer-events: none;
        z-index: 999997;
        animation: boss-magical-glow 3s ease-in-out infinite;
      }

      /* Floating sparkles background */
      body.boss-effect-tyrant::after {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background-image:
          radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.4) 0%, transparent 2px),
          radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.4) 0%, transparent 2px),
          radial-gradient(circle at 40% 80%, rgba(255, 255, 255, 0.4) 0%, transparent 2px),
          radial-gradient(circle at 60% 20%, rgba(255, 255, 255, 0.4) 0%, transparent 2px);
        background-size: 100% 100%;
        pointer-events: none;
        z-index: 999996;
        animation: boss-sparkle-twinkle 2s ease-in-out infinite;
      }

      @keyframes boss-magical-glow {
        0%, 100% { opacity: 0.7; }
        50% { opacity: 1; }
      }

      @keyframes boss-sparkle-twinkle {
        0%, 100% { opacity: 0.6; }
        50% { opacity: 1; }
      }

      /* Ensure Tiny Tyrant NEVER shakes */
      body.no-screen-shake.boss-effect-tyrant {
        animation: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  private resizeCanvas(): void {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
  }

  private animate = (): void => {
    if (!this.enabled || !this.ctx) {
      // If disabled during animation, ensure cleanup happens
      if (!this.enabled && this.animationFrameId) {
        this.cleanup();
      }
      return;
    }

    this.time += 0.016; // ~60fps

    // Smooth intensity transition
    if (this.intensity !== this.targetIntensity) {
      const delta = this.targetIntensity - this.intensity;
      const step = this.fadeSpeed * 0.016; // Per frame
      if (Math.abs(delta) < step) {
        this.intensity = this.targetIntensity;
      } else {
        this.intensity += Math.sign(delta) * step;
      }
    }

    // Check if fade-out is complete and cleanup should happen
    if (this.targetIntensity === 0 && this.intensity <= 0.01) {
      this.intensity = 0;
      this.cleanup();
      return;
    }

    // Update overlay opacity
    this.overlay.style.opacity = this.intensity.toString();

    const width = this.canvas.width;
    const height = this.canvas.height;

    // Clear canvas
    this.ctx.clearRect(0, 0, width, height);

    // Apply intensity to effects
    const alpha = this.intensity;

    // Draw variant-specific animated effects
    switch (this.currentVariant) {
      case 0: // Tiny Tyrant - Magical sparkles
        this.drawTyrantEffect(width, height, alpha);
        break;
      case 1: // Colossus - Impact particles
        this.drawColossusEffect(width, height, alpha);
        break;
      case 2: // Swarm Queen - Pixelated particles
        this.drawSwarmEffect(width, height, alpha);
        break;
      case 3: // Void Construct - Void particles
        this.drawVoidEffect(width, height, alpha);
        break;
      case 4: // Omega Core - Energy lightning
        this.drawOmegaEffect(width, height, alpha);
        break;
    }

    this.animationFrameId = requestAnimationFrame(this.animate);
  };

  private drawColossusEffect(width: number, height: number, alpha: number): void {
    if (!this.ctx) return;

    // Heavy impact rocks falling
    const particleCount = 20;
    for (let i = 0; i < particleCount; i++) {
      const x = ((i * 137.5 + this.time * 50) % width);
      const y = ((this.time * 100 + i * 50) % height);
      const size = 3 + Math.sin(this.time + i) * 2;

      this.ctx.fillStyle = `rgba(139, 0, 0, ${alpha * 0.6})`;
      this.ctx.fillRect(x, y, size, size);

      // Glow
      this.ctx.fillStyle = `rgba(255, 69, 0, ${alpha * 0.3})`;
      this.ctx.fillRect(x - 1, y - 1, size + 2, size + 2);
    }

    // Vignette
    const gradient = this.ctx.createRadialGradient(
      width / 2, height / 2, 0,
      width / 2, height / 2, width / 2
    );
    gradient.addColorStop(0, `rgba(0, 0, 0, 0)`);
    gradient.addColorStop(0.7, `rgba(139, 0, 0, ${alpha * 0.2})`);
    gradient.addColorStop(1, `rgba(139, 0, 0, ${alpha * 0.5})`);
    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(0, 0, width, height);
  }

  private drawSwarmEffect(width: number, height: number, alpha: number): void {
    if (!this.ctx) return;

    // Glitchy pixelated particles
    const pixelSize = 4;
    const particleCount = 30;

    for (let i = 0; i < particleCount; i++) {
      const x = Math.floor(((i * 234.5 + this.time * 80) % width) / pixelSize) * pixelSize;
      const y = Math.floor(((this.time * 60 + i * 70) % height) / pixelSize) * pixelSize;
      const glitchOffset = Math.sin(this.time * 10 + i) > 0.7 ? pixelSize : 0;

      this.ctx.fillStyle = `rgba(75, 0, 130, ${alpha * 0.5})`;
      this.ctx.fillRect(x + glitchOffset, y, pixelSize * 2, pixelSize * 2);

      // Purple accent
      this.ctx.fillStyle = `rgba(147, 112, 219, ${alpha * 0.4})`;
      this.ctx.fillRect(x, y + pixelSize, pixelSize, pixelSize);
    }

    // Scanlines
    this.ctx.fillStyle = `rgba(75, 0, 130, ${alpha * 0.1})`;
    for (let y = 0; y < height; y += 4) {
      this.ctx.fillRect(0, y + (this.time * 100) % 8, width, 2);
    }
  }

  private drawVoidEffect(width: number, height: number, alpha: number): void {
    if (!this.ctx) return;

    // Floating cyan particles scattered randomly
    const particleCount = 50;
    for (let i = 0; i < particleCount; i++) {
      const seed = i * 137.5; // Golden angle for distribution
      const x = ((seed + this.time * 30) % width);
      const y = ((seed * 1.7 + this.time * 20) % height);
      const size = 1.5 + Math.sin(this.time * 3 + i) * 1;
      const particleAlpha = alpha * (0.5 + Math.sin(this.time * 2 + i * 0.5) * 0.3);

      // Cyan glow particles
      this.ctx.fillStyle = `rgba(0, 255, 255, ${particleAlpha * 0.9})`;
      this.ctx.beginPath();
      this.ctx.arc(x, y, size, 0, Math.PI * 2);
      this.ctx.fill();

      // Outer glow
      this.ctx.fillStyle = `rgba(100, 255, 255, ${particleAlpha * 0.4})`;
      this.ctx.beginPath();
      this.ctx.arc(x, y, size * 2.5, 0, Math.PI * 2);
      this.ctx.fill();
    }

    // Lighter shadow particles for depth
    const shadowCount = 30;
    for (let i = 0; i < shadowCount; i++) {
      const seed = i * 222.5;
      const x = ((seed + this.time * 15) % width);
      const y = ((seed * 1.3 - this.time * 10) % height);
      const size = 2 + Math.sin(this.time * 2 + i * 0.3) * 1.5;

      this.ctx.fillStyle = `rgba(0, 100, 150, ${alpha * 0.2})`;
      this.ctx.beginPath();
      this.ctx.arc(x, y, size, 0, Math.PI * 2);
      this.ctx.fill();
    }

    // Random void rifts (cyan streaks instead of dark)
    const riftCount = 5;
    for (let i = 0; i < riftCount; i++) {
      const x1 = (i * width / riftCount + this.time * 10) % width;
      const y1 = (i * 100 + Math.sin(this.time + i) * 50) % height;
      const x2 = x1 + 40 + Math.sin(this.time * 2 + i) * 25;
      const y2 = y1 + 100;

      const gradient = this.ctx.createLinearGradient(x1, y1, x2, y2);
      gradient.addColorStop(0, `rgba(0, 255, 255, ${alpha * 0.15})`);
      gradient.addColorStop(0.5, `rgba(0, 200, 255, ${alpha * 0.25})`);
      gradient.addColorStop(1, `rgba(0, 100, 200, ${alpha * 0.1})`);

      this.ctx.strokeStyle = gradient;
      this.ctx.lineWidth = 2;
      this.ctx.beginPath();
      this.ctx.moveTo(x1, y1);
      this.ctx.lineTo(x2, y2);
      this.ctx.stroke();
    }
  }

  private drawOmegaEffect(width: number, height: number, alpha: number): void {
    if (!this.ctx) return;

    // Energy lightning bolts - Reduced
    const boltCount = 4; // Reduced from 8
    for (let i = 0; i < boltCount; i++) {
      const startX = Math.random() * width;
      const startY = 0;
      let x = startX;
      let y = startY;

      this.ctx.strokeStyle = `rgba(255, 255, 0, ${alpha * 0.2})`; // Reduced from 0.6
      this.ctx.lineWidth = 1; // Reduced from 2
      this.ctx.beginPath();
      this.ctx.moveTo(x, y);

      // Random lightning path
      while (y < height) {
        x += (Math.random() - 0.5) * 50;
        y += Math.random() * 50 + 20;
        this.ctx.lineTo(x, y);
      }

      if (Math.sin(this.time * 20 + i) > 0.9) { // Changed from 0.8 to appear less often
        this.ctx.stroke();
      }
    }

    // Energy particles - Reduced
    const particleCount = 25; // Reduced from 50
    for (let i = 0; i < particleCount; i++) {
      const x = ((i * 197.3 + this.time * 120) % width);
      const y = ((this.time * 80 + i * 40) % height);
      const size = 2 + Math.sin(this.time * 5 + i) * 1;

      // Red/Yellow/Orange energy - Reduced opacity
      const color = i % 3 === 0 ? 'rgba(255, 0, 0' : i % 3 === 1 ? 'rgba(255, 255, 0' : 'rgba(255, 165, 0';
      this.ctx.fillStyle = `${color}, ${alpha * 0.3})`; // Reduced from 0.7
      this.ctx.beginPath();
      this.ctx.arc(x, y, size, 0, Math.PI * 2);
      this.ctx.fill();

      // Glow - Reduced
      this.ctx.fillStyle = `${color}, ${alpha * 0.1})`; // Reduced from 0.3
      this.ctx.beginPath();
      this.ctx.arc(x, y, size * 2, 0, Math.PI * 2);
      this.ctx.fill();
    }

    // Energy pulse overlay - Heavily reduced
    const pulseAlpha = Math.sin(this.time * 4) * 0.5 + 0.5;
    const gradient = this.ctx.createRadialGradient(
      width / 2, height / 2, 0,
      width / 2, height / 2, width / 2
    );
    gradient.addColorStop(0, `rgba(255, 255, 0, ${alpha * pulseAlpha * 0.05})`); // Reduced from 0.2
    gradient.addColorStop(1, `rgba(255, 0, 0, ${alpha * pulseAlpha * 0.03})`); // Reduced from 0.1
    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(0, 0, width, height);
  }

  private drawTyrantEffect(width: number, height: number, alpha: number): void {
    if (!this.ctx) return;

    // Magical rainbow sparkles floating gently
    const sparkleCount = 40;
    for (let i = 0; i < sparkleCount; i++) {
      const x = ((i * 234.7 + this.time * 40) % width);
      const y = ((i * 117.3 + Math.sin(this.time + i) * 50) % height);
      const size = 2 + Math.sin(this.time * 2 + i) * 1.5;
      const twinkle = Math.abs(Math.sin(this.time * 3 + i * 0.5));

      // Rainbow color cycling
      const hue = (this.time * 50 + i * 30) % 360;
      this.ctx.fillStyle = `hsla(${hue}, 70%, 75%, ${alpha * twinkle * 0.8})`;

      // Draw 4-pointed star
      this.ctx.save();
      this.ctx.translate(x, y);
      this.ctx.rotate(this.time + i);
      this.ctx.beginPath();
      for (let j = 0; j < 4; j++) {
        const angle = (j / 4) * Math.PI * 2;
        const r = j % 2 === 0 ? size : size * 0.4;
        this.ctx.lineTo(Math.cos(angle) * r, Math.sin(angle) * r);
      }
      this.ctx.closePath();
      this.ctx.fill();
      this.ctx.restore();
    }

    // Floating heart particles
    const heartCount = 8;
    for (let i = 0; i < heartCount; i++) {
      const x = ((i * width / heartCount + this.time * 30) % width);
      const y = height - ((this.time * 50 + i * 80) % (height + 100));
      const heartSize = 6 + Math.sin(this.time + i) * 2;
      const heartAlpha = alpha * (0.4 + Math.sin(this.time * 2 + i) * 0.2);

      this.ctx.fillStyle = `rgba(255, 182, 193, ${heartAlpha})`; // Light pink
      this.ctx.save();
      this.ctx.translate(x, y);
      this.ctx.scale(1, -1); // Flip for proper heart orientation

      // Draw heart shape
      this.ctx.beginPath();
      this.ctx.moveTo(0, heartSize * 0.3);
      this.ctx.bezierCurveTo(
        -heartSize * 0.5, -heartSize * 0.3,
        -heartSize, heartSize * 0.3,
        0, heartSize
      );
      this.ctx.bezierCurveTo(
        heartSize, heartSize * 0.3,
        heartSize * 0.5, -heartSize * 0.3,
        0, heartSize * 0.3
      );
      this.ctx.closePath();
      this.ctx.fill();
      this.ctx.restore();
    }

    // Soft magical glow overlay
    const gradient = this.ctx.createRadialGradient(
      width / 2, height / 2, 0,
      width / 2, height / 2, width / 2
    );
    gradient.addColorStop(0, `rgba(255, 255, 255, ${alpha * 0.05})`);
    gradient.addColorStop(0.5, `rgba(168, 230, 207, ${alpha * 0.08})`);
    gradient.addColorStop(1, `rgba(255, 170, 165, ${alpha * 0.06})`);
    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(0, 0, width, height);
  }

  /**
   * Enable boss effect with smooth fade-in
   * @param variant Boss variant (0=Colossus, 1=Swarm, 2=Void, 3=Omega)
   * @param fadeDuration Duration of fade-in in seconds (default: 1.0)
   */
  enable(variant: BossVariant, fadeDuration: number = 1.0): void {
    if (this.enabled && this.currentVariant === variant) return;

    // Clear any pending cleanup timeout
    if (this.cleanupTimeoutId !== null) {
      clearTimeout(this.cleanupTimeoutId);
      this.cleanupTimeoutId = null;
    }

    this.currentVariant = variant;
    this.enabled = true;
    this.fadeSpeed = 1.0 / fadeDuration;
    this.targetIntensity = 1.0;

    // Show overlay
    this.overlay.style.display = 'block';

    // Remove all boss effect classes
    document.body.classList.remove(
      'boss-effect-tyrant',
      'boss-effect-colossus',
      'boss-effect-swarm',
      'boss-effect-void',
      'boss-effect-omega'
    );

    // Add appropriate class
    const classNames: Record<BossVariant, string> = {
      0: 'boss-effect-tyrant',
      1: 'boss-effect-colossus',
      2: 'boss-effect-swarm',
      3: 'boss-effect-void',
      4: 'boss-effect-omega'
    };
    document.body.classList.add(classNames[variant]);

    // Start animation
    if (!this.animationFrameId) {
      this.animate();
    }
  }

  /**
   * Disable boss effect with smooth fade-out
   * @param fadeDuration Duration of fade-out in seconds (default: 1.0)
   */
  disable(fadeDuration: number = 1.0): void {
    if (!this.enabled) return;

    // Clear any existing cleanup timeout
    if (this.cleanupTimeoutId !== null) {
      clearTimeout(this.cleanupTimeoutId);
      this.cleanupTimeoutId = null;
    }

    this.fadeSpeed = 1.0 / fadeDuration;
    this.targetIntensity = 0;

    // Fallback: Ensure cleanup happens after fade duration + buffer
    // This prevents effects from staying if animation loop stops
    this.cleanupTimeoutId = window.setTimeout(() => {
      this.cleanup();
      this.cleanupTimeoutId = null;
    }, fadeDuration * 1000 + 200);
  }

  private cleanup(): void {
    // Clear cleanup timeout if it exists
    if (this.cleanupTimeoutId !== null) {
      clearTimeout(this.cleanupTimeoutId);
      this.cleanupTimeoutId = null;
    }

    this.enabled = false;
    this.targetIntensity = 0;
    this.intensity = 0;
    this.overlay.style.display = 'none';
    this.overlay.style.opacity = '0';

    // Remove all boss effect classes
    document.body.classList.remove(
      'boss-effect-colossus',
      'boss-effect-swarm',
      'boss-effect-void',
      'boss-effect-omega',
      'boss-effect-tyrant'
    );

    // Stop animation
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Clear canvas
    if (this.ctx) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
  }

  /**
   * Check if effect is currently enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Get current boss variant
   */
  getCurrentVariant(): BossVariant {
    return this.currentVariant;
  }

  /**
   * Get current intensity (0 to 1)
   */
  getIntensity(): number {
    return this.intensity;
  }
}

